<template>
  <div v-if="progressCards.length" v-loading="progressLoading" class="member-level-progress-block py-6 ">
    <!-- <p class="font-semibold text-gray-100 mb-3">會員進度條</p> -->
    <el-carousel :autoplay="false" height="200px" arrow="always" indicator-position="outside">
      <el-carousel-item v-for="(group, idx) in progressCardGroups" :key="idx">
        <div class="flex gap-3 h-full px-[50px]">
          <ConditionCard
            v-for="card in group"
            :key="card.key"
            class="flex-1 min-w-0"
            :condition="card.condition"
            :current-level-name="card.currentLevelName"
            :target-level-name="card.targetLevelName"
            :is-renewal="card.isRenewal"
            :expired-at="progressData ? progressData.levelExpiredAt : null"
            :custom-text="card.customText"
          />
        </div>
      </el-carousel-item>
    </el-carousel>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { get, keyBy } from 'lodash'
import { useShop } from '@/use/shop'
import { useFetch } from '@/use/fetch'
import { t } from '@/plugins/i18n/i18n'
import { GetMemberLevelProgress, GetMemberLevelConfig } from '@/api/memberLevel'
import { GetMemberLevelRule } from '@/api/memberLevelBenefitSettings'
import ConditionCard from '@/views/Members/components/MemberLevelProgressDrawer/components/ConditionCard.vue'

export default defineComponent({
  name: 'MemberLevelProgressBlock',
  components: { ConditionCard },
  props: {
    memberId: { type: String, required: true },
  },
  setup (props) {
    const { shopId } = useShop()
    const { simpleFetch } = useFetch()

    const progressData = ref(null)
    const levelConfigMap = ref({})
    const levelRuleData = ref(null)
    const progressLoading = ref(false)

    const buildProgressSettingKey = (currentLevel, targetLevel, conditionId) => {
      return `${currentLevel}-${targetLevel}-${conditionId}`
    }

    const buildProgressGroupKey = (currentLevel, targetLevel) => {
      return `${currentLevel}-${targetLevel}`
    }

    const getProgressLevelName = (level) => {
      return get(levelConfigMap.value, [level, 'name'], `${t('member.tab.memberInfo.memberLevel')} ${level}`)
    }

    const progressCurrentLevelName = computed(() => {
      if (!progressData.value) return ''
      return getProgressLevelName(progressData.value.currentLevel)
    })

    const adminVisibleUpgradeConditionKeys = computed(() => {
      const items = get(levelRuleData.value, 'progressBarSettings.upgradeProgress', [])
      return new Set(
        items
          .filter((item) => (item.displayLocations || []).includes('adminMemberManage'))
          .map((item) => buildProgressSettingKey(item.currentLevel, item.targetLevel, item.conditionId)),
      )
    })

    const configuredUpgradeGroupKeys = computed(() => {
      const items = get(levelRuleData.value, 'progressBarSettings.upgradeProgress', [])
      return new Set(
        items.map((item) => buildProgressGroupKey(item.currentLevel, item.targetLevel)),
      )
    })

    const adminVisibleRenewalConditionKeys = computed(() => {
      const items = get(levelRuleData.value, 'progressBarSettings.renewalProgress', [])
      return new Set(
        items
          .filter((item) => (item.displayLocations || []).includes('adminMemberManage'))
          .map((item) => buildProgressSettingKey(item.currentLevel, item.targetLevel, item.conditionId)),
      )
    })

    const configuredRenewalGroupKeys = computed(() => {
      const items = get(levelRuleData.value, 'progressBarSettings.renewalProgress', [])
      return new Set(
        items.map((item) => buildProgressGroupKey(item.currentLevel, item.targetLevel)),
      )
    })

    const upgradeCustomTextMap = computed(() => {
      const items = get(levelRuleData.value, 'progressBarSettings.upgradeProgress', [])
      return items.reduce((map, item) => {
        map[buildProgressSettingKey(item.currentLevel, item.targetLevel, item.conditionId)] = item.customText ?? null
        return map
      }, {})
    })

    const renewalCustomTextMap = computed(() => {
      const items = get(levelRuleData.value, 'progressBarSettings.renewalProgress', [])
      return items.reduce((map, item) => {
        map[buildProgressSettingKey(item.currentLevel, item.targetLevel, item.conditionId)] = item.customText ?? null
        return map
      }, {})
    })

    const progressCards = computed(() => {
      if (!progressData.value) return []
      const cards = []
      const upgradeGroups = get(progressData.value, 'upgradeProgress', [])
      const currentLevel = get(progressData.value, 'currentLevel', 0)
      for (const group of upgradeGroups) {
        const targetLevelName = getProgressLevelName(group.targetLevel)
        const groupKey = buildProgressGroupKey(currentLevel, group.targetLevel)
        const hasConfiguredGroup = configuredUpgradeGroupKeys.value.has(groupKey)
        for (const cond of group.conditionProgress) {
          if (
            hasConfiguredGroup &&
            !adminVisibleUpgradeConditionKeys.value.has(
              buildProgressSettingKey(currentLevel, group.targetLevel, cond.conditionId),
            )
          ) continue
          cards.push({
            key: `upgrade-${group.targetLevel}-${cond.conditionId}`,
            condition: cond,
            currentLevelName: progressCurrentLevelName.value,
            targetLevelName,
            isRenewal: false,
            customText: cond.customText ?? upgradeCustomTextMap.value[
              buildProgressSettingKey(currentLevel, group.targetLevel, cond.conditionId)
            ] ?? null,
          })
        }
      }
      const renewalGroup = get(progressData.value, 'renewalProgress')
      if (renewalGroup) {
        const targetLevelName = getProgressLevelName(renewalGroup.targetLevel)
        const renewalGroupKey = buildProgressGroupKey(currentLevel, renewalGroup.targetLevel)
        const hasConfiguredGroup = configuredRenewalGroupKeys.value.has(renewalGroupKey)
        for (const cond of renewalGroup.conditionProgress) {
          if (
            hasConfiguredGroup &&
            !adminVisibleRenewalConditionKeys.value.has(
              buildProgressSettingKey(currentLevel, renewalGroup.targetLevel, cond.conditionId),
            )
          ) continue
          cards.push({
            key: `renewal-${renewalGroup.targetLevel}-${cond.conditionId}`,
            condition: cond,
            currentLevelName: progressCurrentLevelName.value,
            targetLevelName,
            isRenewal: true,
            customText: cond.customText ?? renewalCustomTextMap.value[
              buildProgressSettingKey(currentLevel, renewalGroup.targetLevel, cond.conditionId)
            ] ?? null,
          })
        }
      }
      return cards
    })

    const progressCardGroups = computed(() => {
      const groups = []
      for (let i = 0; i < progressCards.value.length; i += 2) {
        groups.push(progressCards.value.slice(i, i + 2))
      }
      return groups
    })

    const refresh = async () => {
      if (!props.memberId) return
      progressLoading.value = true
      await Promise.all([
        simpleFetch(
          GetMemberLevelProgress,
          { shopId: shopId.value, memberId: props.memberId },
          (res) => { progressData.value = res },
        ),
        simpleFetch(
          GetMemberLevelConfig,
          { shopId: shopId.value },
          (res) => { levelConfigMap.value = keyBy(Array.isArray(res) ? res : [], 'level') },
        ),
        simpleFetch(
          GetMemberLevelRule,
          { shopId: shopId.value },
          (res) => { levelRuleData.value = res },
        ),
      ])
      progressLoading.value = false
    }

    onMounted(() => {
      refresh()
    })

    return {
      progressData,
      progressCards,
      progressCardGroups,
      progressLoading,
      refresh,
    }
  },
})
</script>

<style scoped lang="postcss">
.member-level-progress-block {

  :deep(.el-carousel__arrow) {
    @apply bg-gray-20 text-gray-100 border border-gray-40;
    &:hover {
      @apply bg-gray-40;
    }
  }

  :deep(.el-carousel__arrow--left) {
    left: 0;
  }

  :deep(.el-carousel__arrow--right) {
    right: 0;
  }
}
</style>
